import { Email, FirstName, LastName, TrimmedString } from "@albertine/types";
import type {
    AgentListItem,
    AgentTeam,
    CreateAgentRequest,
    UpdateAgentRequest,
} from "@albertine/types/openapi/lmt";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormField, FormInput } from "@prototypes/form-field/FormField";
import { X } from "lucide-react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { AgentTeamSelect } from "./AgentTeamSelect";

const SharedAgentFormFields = {
    firstName: FirstName,
    lastName: LastName,
    teamId: z.uuid().nullable(),
    slackId: TrimmedString.pipe(z.string().min(1, { error: "Slack ID is needed" })),
    calendlyUrl: z
        .string()
        .trim()
        .pipe(z.url({ error: "Calendly link is not valid" }).or(z.literal(""))),
};

const CreateAgentFormSchema = z.object({
    ...SharedAgentFormFields,
    email: Email,
});

const EditAgentFormSchema = z.object({
    ...SharedAgentFormFields,
    email: z.string(),
});

type AgentForm = z.infer<typeof CreateAgentFormSchema>;

const EMPTY_FORM: AgentForm = {
    firstName: "",
    lastName: "",
    email: "",
    teamId: null,
    calendlyUrl: "",
    slackId: "",
};

type Props = {
    open: boolean;
    teams: AgentTeam[];
    isSaving: boolean;
    agent?: AgentListItem | null;
    onClose: () => void;
    onCreate: (request: CreateAgentRequest) => void;
    onUpdate: (agentId: string, request: UpdateAgentRequest) => void;
};

export function AddAgentDialog({
    open,
    teams,
    isSaving,
    agent = null,
    onClose,
    onCreate,
    onUpdate,
}: Props) {
    const isEditing = !!agent;
    const formSchema = isEditing ? EditAgentFormSchema : CreateAgentFormSchema;

    const { register, control, handleSubmit, watch } = useForm<AgentForm>({
        resolver: zodResolver(formSchema),
        defaultValues: agent ? formValuesFromAgent(agent) : EMPTY_FORM,
        mode: "onChange",
    });

    const blockedReason = firstBlockedReason(watch(), formSchema);

    const onSubmit: SubmitHandler<AgentForm> = (data) => {
        if (agent) {
            onUpdate(agent.id, {
                firstName: data.firstName,
                lastName: data.lastName,
                slackId: data.slackId.trim(),
                onboardingCallReservationUrl: data.calendlyUrl.trim() || null,
                teamId: data.teamId,
            });
            return;
        }

        onCreate({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            slackId: data.slackId.trim(),
            onboardingCallReservationUrl: data.calendlyUrl.trim() || undefined,
            teamId: data.teamId ?? undefined,
        });
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent size="medium" className="bg-white" showCloseButton={false}>
                <form className="contents" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <DialogHeader>
                        <DialogTitle className="heading-4 text-zinc-900">
                            {isEditing ? "Edit agent" : "Add new agent"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex flex-col gap-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <FormField label="First name" htmlFor="agent-first-name">
                                <FormInput
                                    id="agent-first-name"
                                    autoFocus
                                    placeholder="First name"
                                    {...register("firstName")}
                                />
                            </FormField>
                            <FormField label="Last name" htmlFor="agent-last-name">
                                <FormInput
                                    id="agent-last-name"
                                    placeholder="Last name"
                                    {...register("lastName")}
                                />
                            </FormField>
                        </div>

                        <FormField label="Email" htmlFor="agent-email">
                            <FormInput
                                id="agent-email"
                                type="email"
                                placeholder="example@example.com"
                                disabled={isEditing}
                                readOnly={isEditing}
                                {...register("email")}
                            />
                        </FormField>

                        <FormField label="Team" htmlFor="agent-team">
                            <Controller
                                control={control}
                                name="teamId"
                                render={({ field }) => (
                                    <AgentTeamSelect
                                        id="agent-team"
                                        teams={teams}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    />
                                )}
                            />
                        </FormField>

                        <FormField
                            label="Calendly link"
                            htmlFor="agent-calendly"
                            hint="The Calendly link appears in the first welcome message, allowing new members to book an onboarding call."
                        >
                            <FormInput
                                id="agent-calendly"
                                type="url"
                                placeholder="https://www.calendly.com/..."
                                {...register("calendlyUrl")}
                            />
                        </FormField>

                        <FormField
                            label="Slack ID"
                            htmlFor="agent-slack-id"
                            hint='Find the person in Slack, click their avatar, tap the three dots on the right panel, and select "Copy member ID".'
                        >
                            <FormInput
                                id="agent-slack-id"
                                placeholder="Slack ID"
                                {...register("slackId")}
                            />
                        </FormField>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <SubmitButton
                            label={isEditing ? "Save changes" : "Add new agent"}
                            blockedReason={blockedReason}
                            disabled={isSaving}
                        />
                    </DialogFooter>
                </form>

                <DialogClose
                    render={
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className="absolute top-5 right-5"
                            aria-label="Close"
                        />
                    }
                >
                    <X />
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}

function formValuesFromAgent(agent: AgentListItem): AgentForm {
    return {
        firstName: agent.firstName,
        lastName: lastNameFromAgent(agent),
        email: agent.email,
        teamId: agent.teamId,
        calendlyUrl: agent.onboardingCallReservationUrl ?? "",
        slackId: agent.slackId ?? "",
    };
}

function lastNameFromAgent(agent: AgentListItem): string {
    const prefix = `${agent.firstName} `;
    if (agent.name.startsWith(prefix)) {
        return agent.name.slice(prefix.length);
    }
    const space = agent.name.indexOf(" ");
    return space === -1 ? "" : agent.name.slice(space + 1).trim();
}

function firstBlockedReason(
    values: AgentForm,
    schema: typeof CreateAgentFormSchema | typeof EditAgentFormSchema,
): string | null {
    const parsed = schema.safeParse(values);
    if (parsed.success) return null;

    const order = ["firstName", "lastName", "email", "calendlyUrl", "slackId"] as const;
    for (const key of order) {
        const issue = parsed.error.issues.find((entry) => entry.path[0] === key);
        if (issue) return issue.message;
    }
    return parsed.error.issues[0]?.message ?? "Check the form fields";
}

function SubmitButton({
    label,
    blockedReason,
    disabled,
}: {
    label: string;
    blockedReason: string | null;
    disabled: boolean;
}) {
    const button = (
        <Button type="submit" disabled={!!blockedReason || disabled}>
            {label}
        </Button>
    );

    if (!blockedReason) return button;

    return (
        <TooltipProvider delay={200}>
            <Tooltip>
                <TooltipTrigger
                    render={<span className="inline-block">{button}</span>}
                />
                <TooltipContent positionerClassName="z-[600]">
                    {blockedReason}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
